import {AxiosRequestConfig} from 'axios';
import {spotheroGet} from 'api';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys} from 'utils/format-strings';
import {
    ReservationsSegmented,
    ReservationsDailySegmented,
    ReservationsSegmentedResponse,
} from './reservations.types';

/**
 * Formats the response from the get reservation daily segmented endpoint so that it can be consumed by the UI
 * @param data - The response from the get reservation endpoint
 * @return The formatted response
 */
export const formatGetReservationsDailySegmentedResponse = (
    data: ReservationsSegmentedResponse
): ReservationsSegmented => {
    const formattedData: ReservationsSegmented = camelizeKeys(data);

    const transformedSegments: ReservationsDailySegmented[] =
        formattedData.segments.map((segment) => ({
            ...segment,
            transient: segment.transient,
            monthly: segment.monthly,
            event: segment.event,
            id: segment.timePeriod,
        }));

    return {
        ...formattedData,
        segments: transformedSegments,
    };
};

/**
 * Gets an operator's Reservations Per Half Hour
 *
 * @param facilityId - The ID of the facility for which to get segmented reservations
 * @param date - The date for which to retrieve reservation segments
 * @param config - Additional Axios configuration options for the request
 * @returns A promise that resolves to the formatted daily segmented reservations data
 */
const getReservationsDailySegmented = async (
    facilityId: number,
    date: string,
    config: AxiosRequestConfig = {}
) => {
    try {
        const response = await spotheroGet<ReservationsSegmentedResponse>(
            `operator-reservations/facilities/${facilityId}/daily/segmented/`,
            {params: {date}, ...config}
        );

        const formattedResponse = formatGetReservationsDailySegmentedResponse(
            response.data.data
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw new Error(error.message);
    }
};

export default getReservationsDailySegmented;
